import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Users, Loader2, UserCheck, Wand2, RefreshCw } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper to calculate age from birthdate
const calculateAge = (birthdate) => {
  if (!birthdate) return "N/A";
  const today = new Date();
  const birth = new Date(birthdate);
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age--;
  }
  return age;
};

export default function AcademyUnassignedPlayers({ user }) {
  const [players, setPlayers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assigning, setAssigning] = useState(null);
  const [autoAssigning, setAutoAssigning] = useState(false);
  const [lastAutoAssignResult, setLastAutoAssignResult] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [playersRes, teamsRes] = await Promise.all([
        axios.get(`${API}/academy/admin/unassigned-players`, { withCredentials: true }),
        axios.get(`${API}/academy/teams`, { withCredentials: true })
      ]);
      setPlayers(playersRes.data || []);
      setTeams(teamsRes.data || []);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleAutoAssign = async () => {
    setAutoAssigning(true);
    try {
      const response = await axios.post(
        `${API}/academy/admin/auto-assign-teams`,
        {},
        { withCredentials: true }
      );
      setLastAutoAssignResult(response.data);
      toast.success(`Auto-assigned ${response.data.assigned_count} players!`);
      fetchData(); // Refresh the list
    } catch (error) {
      console.error("Error auto-assigning:", error);
      toast.error("Failed to auto-assign players");
    } finally {
      setAutoAssigning(false);
    }
  };

  const handleAssignToTeam = async (playerId, teamId) => {
    if (!teamId) return;
    
    setAssigning(playerId);
    try {
      await axios.post(
        `${API}/academy/admin/assign-player-to-team?player_id=${playerId}&team_id=${teamId}`,
        {},
        { withCredentials: true }
      );
      toast.success("Player assigned to team successfully!");
      fetchData(); // Refresh the list
    } catch (error) {
      console.error("Error assigning player:", error);
      toast.error("Failed to assign player to team");
    } finally {
      setAssigning(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0f1a] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0f1a]">
      {/* Header */}
      <header className="bg-slate-950 border-b border-white/5 shadow-lg">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Link to="/academy">
              <Button variant="ghost" className="text-white hover:text-blue-200">
                <ArrowLeft className="w-5 h-5 mr-2" />
                Back to Academy
              </Button>
            </Link>
            <h1 className="text-2xl font-bold text-white">👥 Unassigned Players</h1>
          </div>
          <div className="flex items-center gap-3">
            <Badge className="bg-orange-500/100 text-white text-lg px-4 py-1">
              {players.length} player{players.length !== 1 ? 's' : ''} pending
            </Badge>
            <Button
              onClick={fetchData}
              variant="outline"
              size="sm"
              className="border-slate-600"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            <Button
              onClick={handleAutoAssign}
              disabled={autoAssigning || players.length === 0}
              className="bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700"
            >
              {autoAssigning ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Auto-Assigning...
                </>
              ) : (
                <>
                  <Wand2 className="w-4 h-4 mr-2" />
                  Auto-Assign All
                </>
              )}
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8">
        {/* Auto-Assign Results */}
        {lastAutoAssignResult && (
          <Card className="p-4 mb-6 bg-slate-800/50 border-slate-700">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold text-white flex items-center gap-2">
                <Wand2 className="w-5 h-5 text-purple-400" />
                Auto-Assignment Results
              </h3>
              <Button variant="ghost" size="sm" onClick={() => setLastAutoAssignResult(null)}>
                ✕
              </Button>
            </div>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="p-3 bg-emerald-500/20 rounded-lg">
                <p className="text-sm text-slate-400">Assigned</p>
                <p className="text-2xl font-bold text-emerald-400">{lastAutoAssignResult.assigned_count}</p>
              </div>
              <div className="p-3 bg-amber-500/20 rounded-lg">
                <p className="text-sm text-slate-400">Skipped</p>
                <p className="text-2xl font-bold text-amber-400">{lastAutoAssignResult.skipped_count}</p>
              </div>
            </div>
            {lastAutoAssignResult.assigned_players?.length > 0 && (
              <div className="mb-3">
                <p className="text-sm font-medium text-emerald-400 mb-2">✓ Assigned Players:</p>
                <div className="text-sm text-slate-300 space-y-1 max-h-32 overflow-y-auto">
                  {lastAutoAssignResult.assigned_players.map((p, i) => (
                    <div key={i} className="flex justify-between bg-slate-700/30 px-2 py-1 rounded">
                      <span>{p.name}</span>
                      <span className="text-emerald-400">→ {p.team_name}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {lastAutoAssignResult.skipped_players?.length > 0 && (
              <div>
                <p className="text-sm font-medium text-amber-400 mb-2">⚠ Skipped (manual assignment needed):</p>
                <div className="text-sm text-slate-300 space-y-1 max-h-32 overflow-y-auto">
                  {lastAutoAssignResult.skipped_players.map((p, i) => (
                    <div key={i} className="flex justify-between bg-slate-700/30 px-2 py-1 rounded">
                      <span>{p.name}</span>
                      <span className="text-amber-400 text-xs">{p.reason}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </Card>
        )}

        <Card className="p-6 bg-slate-900/50 shadow-lg">
          <div className="flex items-center gap-3 mb-6">
            <Users className="w-6 h-6 text-orange-400" />
            <h2 className="text-xl font-bold">Players Awaiting Team Assignment</h2>
          </div>

          {players.length === 0 ? (
            <div className="text-center py-12">
              <UserCheck className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-slate-300">All Players Assigned!</h3>
              <p className="text-slate-400 mt-2">Every registered player has been assigned to a team.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Player Name</TableHead>
                  <TableHead>Age</TableHead>
                  <TableHead>Enrolled Sessions</TableHead>
                  <TableHead>Parent</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Assign to Team</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {players.map((player) => (
                  <TableRow key={player.id}>
                    <TableCell>
                      <div>
                        <p className="font-medium">
                          {player.child_name || player.name || `${player.given_name || ''} ${player.last_name || ''}`}
                        </p>
                        <p className="text-xs text-slate-400">{player.membership_number}</p>
                        <Badge className={player.gender === 'female' ? 'bg-pink-500/20 text-pink-300' : 'bg-blue-500/20 text-blue-300'}>
                          {player.gender === 'female' ? '♀' : '♂'} {calculateAge(player.birthdate)} yrs
                        </Badge>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="font-bold text-lg">
                        {calculateAge(player.birthdate)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        {player.enrolled_sessions?.length > 0 ? (
                          player.enrolled_sessions.map((session, idx) => (
                            <div key={idx} className="text-xs bg-purple-500/20 text-purple-300 px-2 py-1 rounded">
                              <span className="font-bold">{session.day_of_week}</span> - {session.name || session.age_group}
                              <span className="text-slate-400 ml-1">{session.time}</span>
                            </div>
                          ))
                        ) : (
                          <span className="text-slate-500 text-sm">No sessions found</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium">{player.parent_name || "N/A"}</p>
                        <p className="text-xs text-slate-400">{player.parent_email}</p>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm">
                      {player.parent_phone || player.emergency_contact || "N/A"}
                    </TableCell>
                    <TableCell>
                      <div className="space-y-2">
                        {player.suggested_teams?.length > 0 && (
                          <div className="text-xs text-emerald-400 mb-1">
                            Suggested: {player.suggested_teams.map(t => t.name).join(", ")}
                          </div>
                        )}
                        <select
                          className="text-sm border rounded px-3 py-2 min-w-[150px] bg-slate-900/50"
                          defaultValue=""
                          disabled={assigning === player.id}
                          onChange={(e) => handleAssignToTeam(player.id, e.target.value)}
                        >
                          <option value="">Select team...</option>
                          {/* Show suggested teams first */}
                          {player.suggested_teams?.length > 0 && (
                            <optgroup label="Suggested">
                              {player.suggested_teams.map((team) => (
                                <option key={`suggested-${team.id}`} value={team.id}>
                                  ⭐ {team.name}
                                </option>
                              ))}
                            </optgroup>
                          )}
                          <optgroup label="All Teams">
                            {teams.map((team) => (
                              <option key={team.id} value={team.id}>
                                {team.name}
                              </option>
                            ))}
                          </optgroup>
                        </select>
                        {assigning === player.id && (
                          <Loader2 className="w-4 h-4 animate-spin inline ml-2" />
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Card>

        {/* Quick Info */}
        <Card className="p-4 mt-6 bg-blue-500/10 border-blue-500/30">
          <p className="text-sm text-blue-400">
            <strong>Tip:</strong> Players are typically assigned to teams based on their age group 
            (e.g., U8 for 7-8 year olds, U10 for 9-10 year olds). 
            Select the appropriate team from the dropdown to complete their registration.
          </p>
        </Card>
      </div>
    </div>
  );
}
